import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Layout from './components/Layout';
import { useInactivityLogout, INACTIVITY_TIMEOUT_MS } from './hooks/useInactivityLogout';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Jobs from './pages/Jobs';
import Logs from './pages/Logs';
import Settings from './pages/Settings';
import Users from './pages/Users';
import Restores from './pages/Restores';
import AuditLog from './pages/AuditLog';
import Resellers from './pages/Resellers';
import Profile from './pages/Profile';
import Setup from './pages/Setup';
import Updates from './pages/Updates';
import { api } from './api';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const BrandingContext = createContext({ platform_name: 'OpenBackup', logo_url: '', favicon_url: '' });
export const useBranding = () => useContext(BrandingContext);

export default function App() {
  const [auth, setAuth]         = useState(null);
  const [me, setMe]             = useState(null);
  const [loading, setLoading]   = useState(true);
  const [firstRun, setFirstRun] = useState(false);
  const [branding, setBranding] = useState({ platform_name: 'OpenBackup', logo_url: '', favicon_url: '' });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const last = parseInt(localStorage.getItem('last_activity') || '0', 10);
      const now  = Date.now();
      if (last && (now - last) > INACTIVITY_TIMEOUT_MS) {
        localStorage.removeItem('token');
        localStorage.removeItem('last_activity');
        sessionStorage.setItem('logout_reason', 'inactivity');
        setAuth(null);
        setLoading(false);
        return;
      }
      localStorage.setItem('last_activity', now.toString());
      setAuth({ token });
      api.me().then(setMe).catch(() => {
        localStorage.removeItem('token');
        setAuth(null);
      }).finally(() => setLoading(false));
    } else {
      // Verificar si es el primer arranque (sin usuarios)
      api.checkFirstRun().then(data => {
        if (data.first_run) setFirstRun(true);
      }).catch(() => {}).finally(() => setLoading(false));
    }
  }, []);

  // Load global branding on mount
  useEffect(() => {
    api.getBranding().then(b => {
      setBranding(b);
      applyBranding(b);
    }).catch(() => {});
  }, []);

  // Reload branding after auth (resellers get their own branding)
  useEffect(() => {
    if (auth && me) {
      api.getBrandingMe().then(b => {
        setBranding(b);
        applyBranding(b);
      }).catch(() => {});
    }
  }, [me]);

  const handleLogin = (data) => {
    // BrowserRouter conserva la ubicación del navegador: sin esto, el login de otro usuario
    // arranca en la ruta que dejó el anterior (p. ej. /clients/<id> ajeno → 403). Es el único
    // punto por el que pasan todos los logins (común, MFA, términos, wizard). La recarga con
    // token válido no pasa por acá, así que conserva su ruta. BrowserRouter se monta recién
    // después del login, así que lee ya la ruta nueva.
    window.history.replaceState(null, '', '/dashboard');
    setAuth(data);
    api.me().then(setMe).catch(() => {
      localStorage.removeItem('token');
      setAuth(null);
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('last_activity');
    setAuth(null);
    setMe(null);
  };

  useInactivityLogout(
    (reason) => {
      localStorage.removeItem('token');
      localStorage.removeItem('last_activity');
      if (reason === 'inactivity') {
        sessionStorage.setItem('logout_reason', 'inactivity');
      }
      window.location.href = '/';
    },
    !!localStorage.getItem('token'),
  );

  const applyBranding = (b) => {
    if (b.platform_name) document.title = b.platform_name;
    if (b.favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = b.favicon_url;
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
      CARGANDO...
    </div>
  );

  if (firstRun) return <Setup onComplete={handleLogin} />;

  if (window.location.pathname === '/reset-password') return <ResetPassword />;

  if (!auth) return <Login onLogin={handleLogin} />;

  // Con sesión pero sin /me todavía no se arma nada: con el rol vacío Dashboard caería en
  // AdminDashboard y dispararía pedidos sólo-admin (403 en la auditoría) para reseller y cliente.
  if (!me) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', fontFamily: 'var(--mono)', color: 'var(--muted)' }}>
      CARGANDO...
    </div>
  );

  const isAdmin      = me?.role === 'admin';
  const isOperator   = me?.role === 'operator';
  const isClient     = me?.role === 'client';
  const isReseller   = me?.role === 'reseller';
  const perms        = me?.permissions || {};
  const resellerPerms = me?.reseller_permissions || {};

  return (
    <BrowserRouter>
      <BrandingContext.Provider value={{ branding, setBranding, applyBranding }}>
        <AuthContext.Provider value={{ me, isAdmin, isOperator, isClient, isReseller, perms, resellerPerms }}>
          <Layout onLogout={handleLogout}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/clients" element={isClient ? <Navigate to={`/clients/${me?.client_id}`} /> : (isReseller || isAdmin || isOperator) ? <Clients /> : <Navigate to="/dashboard" />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/logs" element={<Logs />} />
              <Route path="/settings" element={isAdmin ? <Settings /> : <Navigate to="/dashboard" />} />
              <Route path="/users" element={
                isAdmin || (isReseller && (resellerPerms?.can_manage_users || resellerPerms?.can_manage_account))
                  ? <Users />
                  : <Navigate to="/dashboard" />
              } />
              <Route path="/restores" element={<Restores />} />
              <Route path="/updates" element={(isAdmin || isOperator) ? <Updates /> : <Navigate to="/dashboard" />} />
              <Route path="/resellers" element={isAdmin ? <Resellers /> : <Navigate to="/dashboard" />} />
              <Route path="/audit" element={isAdmin ? <AuditLog /> : <Navigate to="/dashboard" />} />
              <Route path="/profile" element={<Profile />} />
            </Routes>
          </Layout>
        </AuthContext.Provider>
      </BrandingContext.Provider>
    </BrowserRouter>
  );
}

